/**
 * Every word a printed document says, in both languages (PART 3).
 *
 * WHY NOT i18next. The web app's dictionary lives in `apps/web/src/i18n` and
 * is loaded by a React runtime. A PDF is rendered by a background worker with
 * no React, no browser and no user session — and the language it renders in is
 * the RECIPIENT's, not whoever pressed a button (the same rule
 * `email_outbox.language` exists for). So document wording is a plain table,
 * looked up by locale, usable identically from the worker and from the
 * browser's print iframe.
 *
 * Adding a key means adding both languages: the type makes an English-only
 * entry a compile error, because a document that silently falls back to
 * English for one label is worse than one that never had it.
 */
export const DOCUMENT_LOCALES = ['en', 'ar'];
/** RTL is a property of the locale, and the only place that fact is written. */
export function documentDirection(locale) {
    return locale === 'ar' ? 'rtl' : 'ltr';
}
const STRINGS = {
    // ── shared chrome ────────────────────────────────────────────────────────
    page: { en: 'Page', ar: 'صفحة' },
    of: { en: 'of', ar: 'من' },
    printedOn: { en: 'Printed', ar: 'تاريخ الطباعة' },
    /** NFR-33 — the footer names the person who produced the document. */
    printedBy: { en: 'By', ar: 'بواسطة' },
    // ── sales invoice (SAL-04) ───────────────────────────────────────────────
    invoiceTitle: { en: 'Sales Invoice', ar: 'فاتورة مبيعات' },
    invoiceNo: { en: 'Invoice No.', ar: 'رقم الفاتورة' },
    invoiceDate: { en: 'Date', ar: 'التاريخ' },
    dueDate: { en: 'Due date', ar: 'تاريخ الاستحقاق' },
    billTo: { en: 'Bill to', ar: 'العميل' },
    customerCode: { en: 'Customer ID', ar: 'رقم العميل' },
    lineNo: { en: '#', ar: '#' },
    itemCode: { en: 'Code', ar: 'الرمز' },
    description: { en: 'Description', ar: 'البيان' },
    qty: { en: 'Qty', ar: 'الكمية' },
    unitPrice: { en: 'Unit price', ar: 'سعر الوحدة' },
    discountPct: { en: 'Disc.', ar: 'الخصم' },
    lineTotal: { en: 'Total', ar: 'الإجمالي' },
    subtotal: { en: 'Subtotal', ar: 'المجموع الفرعي' },
    discount: { en: 'Discount', ar: 'الخصم' },
    tax: { en: 'VAT', ar: 'ضريبة القيمة المضافة' },
    grandTotal: { en: 'Grand total', ar: 'الإجمالي النهائي' },
    amountPaid: { en: 'Amount paid', ar: 'المبلغ المدفوع' },
    balanceDue: { en: 'Balance due', ar: 'الرصيد المستحق' },
    notes: { en: 'Notes', ar: 'ملاحظات' },
    /** INV-H6's two print options that add a label the template never had. */
    warehouse: { en: 'Warehouse', ar: 'المستودع' },
    bankDetails: { en: 'Bank details', ar: 'البيانات البنكية' },
    statusDraft: { en: 'DRAFT', ar: 'مسودة' },
    statusCancelled: { en: 'CANCELLED', ar: 'ملغاة' },
    // ── partner statement (CRM-04) ───────────────────────────────────────────
    statementCustomer: { en: 'Customer Statement', ar: 'كشف حساب عميل' },
    statementSupplier: { en: 'Supplier Statement', ar: 'كشف حساب مورد' },
    asOf: { en: 'As of', ar: 'حتى تاريخ' },
    openingBalance: { en: 'Opening balance', ar: 'الرصيد الافتتاحي' },
    closingBalance: { en: 'Closing balance', ar: 'الرصيد الختامي' },
    date: { en: 'Date', ar: 'التاريخ' },
    reference: { en: 'Reference', ar: 'المرجع' },
    debit: { en: 'Debit', ar: 'مدين' },
    credit: { en: 'Credit', ar: 'دائن' },
    balance: { en: 'Balance', ar: 'الرصيد' },
    kindInvoice: { en: 'Invoice', ar: 'فاتورة' },
    kindReceipt: { en: 'Receipt', ar: 'سند قبض' },
    kindCreditNote: { en: 'Credit note', ar: 'إشعار دائن' },
    kindPayment: { en: 'Payment', ar: 'سند صرف' },
    kindPurchaseInvoice: { en: 'Purchase invoice', ar: 'فاتورة مشتريات' },
    /** CRM-04 §5f's `debit` badge — PUR-04's supplier return IS the debit note. */
    kindPurchaseReturn: { en: 'Debit note', ar: 'إشعار مدين' },
    noRows: { en: 'No posted documents in this period.', ar: 'لا توجد مستندات مرحّلة في هذه الفترة.' },
    // ── report centres (DSH-05..DSH-08) ──────────────────────────────────────
    period: { en: 'Period', ar: 'الفترة' },
    from: { en: 'From', ar: 'من' },
    to: { en: 'To', ar: 'إلى' },
    vsPrevious: { en: 'vs previous period', ar: 'مقارنة بالفترة السابقة' },
    /**
     * BR-DSH-10-16's heading block: the run time and the person who ran the
     * report, in the heading rather than only the footer, plus the saved
     * configuration's label (DSH-10 Band 6). "الإعداد المحفوظ" follows the web
     * dictionary's own term for a saved report configuration
     * (`reports.savedConfigs`), so the paper and the screen use one word.
     */
    runAt: { en: 'Run', ar: 'وقت التشغيل' },
    runBy: { en: 'Run by', ar: 'تشغيل بواسطة' },
    configuration: { en: 'Configuration', ar: 'الإعداد المحفوظ' },
    // ── wave 2 — the eight record kinds (specs/erp-gap-analysis.md) ─────────
    // Titles. The Arabic follows the web dictionary's own module names so the
    // paper and the sidebar call a document by one word.
    quotationTitle: { en: 'Quotation', ar: 'عرض سعر' },
    salesOrderTitle: { en: 'Sales Order', ar: 'أمر بيع' },
    deliveryNoteTitle: { en: 'Delivery Note', ar: 'سند تسليم' },
    creditNoteTitle: { en: 'Credit Note', ar: 'إشعار دائن' },
    purchaseOrderTitle: { en: 'Purchase Order', ar: 'أمر شراء' },
    purchaseInvoiceTitle: { en: 'Purchase Invoice', ar: 'فاتورة مشتريات' },
    goodsReceiptTitle: { en: 'Goods Receipt Note', ar: 'سند استلام بضاعة' },
    /** PUR-04's supplier return IS the spec's debit note (CRM-04 §5f). */
    purchaseReturnTitle: { en: 'Purchase Return — Debit Note', ar: 'مرتجع مشتريات — إشعار مدين' },
    // Document numbers.
    quotationNo: { en: 'Quotation No.', ar: 'رقم عرض السعر' },
    orderNo: { en: 'Order No.', ar: 'رقم الأمر' },
    deliveryNo: { en: 'Delivery Note No.', ar: 'رقم سند التسليم' },
    creditNoteNo: { en: 'Credit Note No.', ar: 'رقم الإشعار الدائن' },
    purchaseOrderNo: { en: 'PO No.', ar: 'رقم أمر الشراء' },
    billNo: { en: 'Bill No.', ar: 'رقم فاتورة المشتريات' },
    receiptNo: { en: 'GRN No.', ar: 'رقم سند الاستلام' },
    returnNo: { en: 'Return No.', ar: 'رقم المرتجع' },
    // Header fields.
    status: { en: 'Status', ar: 'الحالة' },
    revision: { en: 'Revision', ar: 'المراجعة' },
    validUntil: { en: 'Valid until', ar: 'صالح حتى' },
    terms: { en: 'Terms', ar: 'الشروط' },
    /** Wave 4B: the quotation's "Attn:" line. */
    attention: { en: 'Attention', ar: 'عناية' },
    expectedDate: { en: 'Expected date', ar: 'التاريخ المتوقع' },
    sentOn: { en: 'Sent', ar: 'تاريخ الإرسال' },
    supplierInvoiceNo: { en: 'Supplier invoice no.', ar: 'رقم فاتورة المورد' },
    supplierRef: { en: 'Supplier reference', ar: 'مرجع المورد' },
    // Wave 5C — the goods receipt's lorry paper (PUR-02).
    grnDeliveryNoteNo: { en: 'Supplier delivery note no.', ar: 'رقم سند تسليم المورد' },
    grnReceivedBy: { en: 'Received by', ar: 'استلمها' },
    grnCheckedBy: { en: 'Checked by', ar: 'فحصها' },
    grnDriver: { en: 'Driver', ar: 'السائق' },
    grnVehicleNo: { en: 'Vehicle', ar: 'المركبة' },
    grnManualRef: { en: 'Manual ref.', ar: 'المرجع اليدوي' },
    grnBookNo: { en: 'Book no.', ar: 'رقم الدفتر' },
    // Links to the document this one came from or settles.
    quotationRef: { en: 'Quotation', ar: 'عرض السعر' },
    salesOrderRef: { en: 'Sales order', ar: 'أمر البيع' },
    invoiceRef: { en: 'Invoice', ar: 'الفاتورة' },
    purchaseOrderRef: { en: 'Purchase order', ar: 'أمر الشراء' },
    billRef: { en: 'Purchase invoice', ar: 'فاتورة المشتريات' },
    // Parties.
    customer: { en: 'Customer', ar: 'العميل' },
    supplier: { en: 'Supplier', ar: 'المورد' },
    supplierCode: { en: 'Supplier ID', ar: 'رقم المورد' },
    deliverTo: { en: 'Deliver to', ar: 'التسليم إلى' },
    receivedFrom: { en: 'Received from', ar: 'الاستلام من' },
    // Wave 5B — the delivery note's logistics strip and proof-of-delivery foot.
    driver: { en: 'Driver', ar: 'السائق' },
    vehicleNo: { en: 'Vehicle', ar: 'المركبة' },
    vehicleType: { en: 'Vehicle type', ar: 'نوع المركبة' },
    departedAt: { en: 'Departed', ar: 'وقت المغادرة' },
    deliveryAddress: { en: 'Delivery address', ar: 'عنوان التسليم' },
    receivedBy: { en: 'Received by', ar: 'استلمه' },
    receivedAt: { en: 'Received on', ar: 'تاريخ الاستلام' },
    receiverNote: { en: 'Receiver note', ar: 'ملاحظة المستلم' },
    signature: { en: 'Signature', ar: 'التوقيع' },
    extraLine: { en: 'Extra', ar: 'إضافي' },
    extraLineReason: { en: 'Not on the order', ar: 'غير مدرج في الأمر' },
    // Returns (SAL-05 / PUR-04).
    reason: { en: 'Reason', ar: 'السبب' },
    reasonNote: { en: 'Reason note', ar: 'تفصيل السبب' },
    restocked: { en: 'Returned to stock', ar: 'أُعيد إلى المخزون' },
    valueOnly: { en: 'Value only — no goods movement', ar: 'قيمة فقط — دون حركة بضاعة' },
    yes: { en: 'Yes', ar: 'نعم' },
    no: { en: 'No', ar: 'لا' },
    // Quantity documents (SAL-03 / PUR-02).
    qtyOrdered: { en: 'Ordered', ar: 'المطلوب' },
    qtyDelivered: { en: 'Delivered', ar: 'المُسلَّم' },
    qtyReceived: { en: 'Received', ar: 'المُستلَم' },
    qtyAccepted: { en: 'Accepted', ar: 'المقبول' },
    qtyRemaining: { en: 'Remaining', ar: 'المتبقي' },
    quality: { en: 'Quality', ar: 'الجودة' },
    batchNo: { en: 'Batch', ar: 'الدفعة' },
    serialNos: { en: 'Serial nos.', ar: 'الأرقام التسلسلية' },
    note: { en: 'Note', ar: 'ملاحظة' },
    qualityAccepted: { en: 'Accepted', ar: 'مقبول' },
    qualityRejected: { en: 'Rejected', ar: 'مرفوض' },
    qualityPartiallyAccepted: { en: 'Partially accepted', ar: 'مقبول جزئياً' },
    // Statuses beyond DRAFT / CANCELLED, which the invoice already had.
    statusSent: { en: 'Sent', ar: 'مُرسل' },
    statusAccepted: { en: 'Accepted', ar: 'مقبول' },
    statusRejected: { en: 'Rejected', ar: 'مرفوض' },
    statusExpired: { en: 'Expired', ar: 'منتهي الصلاحية' },
    statusConverted: { en: 'Converted', ar: 'محوَّل' },
    statusConfirmed: { en: 'Confirmed', ar: 'مؤكَّد' },
    statusPartiallyDelivered: { en: 'Partially delivered', ar: 'مُسلَّم جزئياً' },
    statusFulfilled: { en: 'Fulfilled', ar: 'مُنجز' },
    statusClosed: { en: 'Closed', ar: 'مغلق' },
    statusApproved: { en: 'Approved', ar: 'معتمد' },
    statusPartiallyReceived: { en: 'Partially received', ar: 'مُستلَم جزئياً' },
    statusReceived: { en: 'Received', ar: 'مُستلَم' },
    statusPosted: { en: 'Posted', ar: 'مُرحَّل' },
    statusCredited: { en: 'Credited', ar: 'مُقيَّد دائناً' },
    statusRefunded: { en: 'Refunded', ar: 'مُسترَد' },
    // SAL-05 / PUR-04's reason list.
    reasonDamagedGoods: { en: 'Damaged goods', ar: 'بضاعة تالفة' },
    reasonWrongItemShipped: { en: 'Wrong item shipped', ar: 'شُحن صنف خاطئ' },
    reasonCustomerChangedMind: { en: 'Customer changed mind', ar: 'عدل العميل عن الشراء' },
    reasonOverstock: { en: 'Overstock', ar: 'فائض مخزون' },
    reasonOther: { en: 'Other', ar: 'أخرى' },
    // ── import order (PUR-06, wave 5E) ───────────────────────────────────────
    importOrderTitle: { en: 'Import Order', ar: 'أمر استيراد' },
    importRef: { en: 'Import No.', ar: 'رقم الاستيراد' },
    manualRef: { en: 'Supplier / forwarder ref.', ar: 'مرجع المورد / الشاحن' },
    orderDate: { en: 'Order date', ar: 'تاريخ الطلب' },
    shipDate: { en: 'Ship date', ar: 'تاريخ الشحن' },
    arriveDate: { en: 'Arrival date', ar: 'تاريخ الوصول' },
    incoterm: { en: 'Incoterm', ar: 'شرط التسليم' },
    shipMethod: { en: 'Method', ar: 'طريقة الشحن' },
    carrier: { en: 'Carrier', ar: 'الناقل' },
    trackingNo: { en: 'Tracking no.', ar: 'رقم التتبع' },
    exchangeRate: { en: 'Exchange rate', ar: 'سعر الصرف' },
    allocMethod: { en: 'Charges apportioned', ar: 'توزيع المصاريف' },
    methodSea: { en: 'Sea', ar: 'بحري' },
    methodAir: { en: 'Air', ar: 'جوي' },
    methodLand: { en: 'Land', ar: 'بري' },
    allocByValue: { en: 'By value', ar: 'حسب القيمة' },
    allocByQuantity: { en: 'By quantity', ar: 'حسب الكمية' },
    allocByWeight: { en: 'By weight', ar: 'حسب الوزن' },
    allocByVolume: { en: 'By volume', ar: 'حسب الحجم' },
    stageDraft: { en: 'Draft', ar: 'مسودة' },
    stageConfirmed: { en: 'Confirmed', ar: 'مؤكد' },
    stageShipped: { en: 'Shipped', ar: 'تم الشحن' },
    stageInTransit: { en: 'In transit', ar: 'في الطريق' },
    stageCustomsClearance: { en: 'Customs clearance', ar: 'التخليص الجمركي' },
    stageReceived: { en: 'Received', ar: 'مُستلَم' },
    stageClosed: { en: 'Closed', ar: 'مغلق' },
    stageCancelled: { en: 'Cancelled', ar: 'ملغى' },
    stages: { en: 'Stages', ar: 'المراحل' },
    hsCode: { en: 'HS code', ar: 'رمز النظام المنسق' },
    origin: { en: 'Origin', ar: 'المنشأ' },
    lineCost: { en: 'Line cost', ar: 'تكلفة البند' },
    chargeShare: { en: 'Charge share', ar: 'حصة المصاريف' },
    landedUnitCost: { en: 'Landed unit cost', ar: 'التكلفة الواصلة للوحدة' },
    inBase: { en: 'In base', ar: 'بالعملة الأساسية' },
    charges: { en: 'Landed cost charges', ar: 'مصاريف التكلفة الواصلة' },
    chargeFreight: { en: 'Freight', ar: 'الشحن' },
    chargeDuty: { en: 'Customs duty', ar: 'الرسوم الجمركية' },
    chargeInsurance: { en: 'Insurance', ar: 'التأمين' },
    chargeHandling: { en: 'Handling', ar: 'المناولة' },
    chargeOther: { en: 'Other', ar: 'أخرى' },
    amount: { en: 'Amount', ar: 'المبلغ' },
    inOrderCurrency: { en: 'In order currency', ar: 'بعملة الأمر' },
    itemsCost: { en: 'Items cost', ar: 'تكلفة الأصناف' },
    chargesTotal: { en: 'Charges', ar: 'المصاريف' },
    totalLanded: { en: 'Total landed cost', ar: 'إجمالي التكلفة الواصلة' },
    cancelReason: { en: 'Cancelled because', ar: 'سبب الإلغاء' },
};
/** The lookup. `t('invoiceTitle', 'ar')` → "فاتورة مبيعات". */
export function t(key, locale) {
    return STRINGS[key][locale];
}
/** Exposed so a test can assert both languages are complete without reflection tricks. */
export function allDocumentStrings() {
    return STRINGS;
}
//# sourceMappingURL=strings.js.map